import React, { useState, useEffect } from "react";
import axios from 'axios';
import SEO from '../SEO/SEO';
import api from '../../services/api';
import { motion } from "framer-motion";
import * as LucideIcons from 'lucide-react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';





const RiskClassification = () => {
  const [banner, setBanner] = useState(null);
  const [cards, setCards] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const baseUrl = backendUrl.endsWith('/api') ? backendUrl.replace('/api', '') : backendUrl;
  const apiBase = backendUrl.endsWith('/api') ? backendUrl : `${backendUrl}/api`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [bannerRes, cardsRes, tableRes] = await Promise.all([
          api.get('/admin/banners/active?page_type=risk_classification'),
          api.get('/admin/key-benefits/risk_classification_cards/active'),
          api.get('/admin/risk-table/active')
        ]);

        if (bannerRes.data.success && bannerRes.data.data) {
          const banners = bannerRes.data.data;
          if (Array.isArray(banners) && banners.length > 0) {
            setBanner(banners[0]);
          } else if (!Array.isArray(banners)) {
            setBanner(banners);
          }
        }
        if (cardsRes.data.success) {
          const activeCards = (cardsRes.data.data || []).filter(card => card.is_active === 1 || card.is_active === true);
          setCards(activeCards);
        }
        if (tableRes.data.success) {
          setTableData(tableRes.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  
  
  // Schema for SEO
  const riskSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "image": "https://ahamhfc.com/img/loan-banners/Slide-02-1770722226693-126687268.webp?t=1770802805000",
        "description": "AHAM HOUSING FINANCE LIMITED offers home loans across multiple customer segments and regions in India.",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/risk-classification-framework-policy/#webpage",
        "url": "https://ahamhfc.com/risk-classification-framework-policy",
        "name": "Risk Classification & Management Policy - Aham Housing Finance",
        "description": "Explore Aham Housing Finance's risk classification framework, including credit risk models, asset grading, and NHB-compliant risk management practices.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "@id": "https://ahamhfc.com/risk-classification-framework-policy/#primaryimage",
          "contentUrl": "https://ahamhfc.com/img/loan-banners/Slide-02-1770722226693-126687268.webp?t=1770802805000"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://ahamhfc.com/risk-classification-framework-policy/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://ahamhfc.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Risk Classification Policy",
            "item": "https://ahamhfc.com/risk-classification-framework-policy"
          }
        ]
      }
    ]
  };

  
  
  return (
    <div className="bg-white text-gray-800">

      <SEO
        title="Risk Classification & Management Policy | Aham Housing Finance"
        description="Explore Aham Housing Finance’s risk classification framework. Learn about our credit risk models, asset grading, and NHB-compliant risk management standards."
        keywords="Aham Housing Finance risk classification, Aham risk classification, risk classification and management policy, housing finance risk management, credit risk models, asset grading, NHB compliant risk management, borrower risk classification, loan risk assessment, housing finance risk framework, credit underwriting policy, asset quality monitoring, borrower grading system, risk management policy Aham, housing loan risk policy, portfolio risk management, lending risk assessment, customer risk categorisation, risk based lending, Aham Housing Finance policy"
        canonical="https://ahamhfc.com/risk-classification-framework-policy"
        ogTitle="Risk Classification & Management Policy | Aham Housing Finance"
        ogDescription="Explore Aham Housing Finance’s risk classification framework. Learn about our credit risk models, asset grading, and NHB-compliant risk management standards."
        schema={riskSchema}
    />

      {/* ===== Hero Section ===== */}
      {/* ===== Hero Section ===== */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${banner?.image_url ? (banner.image_url.startsWith('http') ? banner.image_url : `${baseUrl}${banner.image_url.startsWith('/') ? '' : '/'}${banner.image_url}`) : '/img/Risk/Risk-classification-Banner.webp'}')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
              {banner?.title || 'Risk Classification'}
            </h1>
            <p className="text-lg md:text-3xl text-white/90 font-medium max-w-4xl leading-relaxed mb-8">
              {banner?.subtitle || 'September 2025 Overview'}
            </p>

            {/* Breadcrumb Navigation */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="flex items-center space-x-2 mb-6 md:mb-8"
            >
              <Link
                to="/"
                className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                Home
              </Link>
              <ChevronRightIcon className="h-4 w-4 text-white/60" />
              <span className="text-yellow-300 text-sm font-semibold">Risk Classification</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== Risk Summary Cards ===== */}
      <section className="max-w-6xl mx-auto px-6 py-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {loading ? (
          [1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-slate-100 rounded-2xl h-40 animate-pulse"></div>
          ))
        ) : (
          cards.length > 0 ? (
            cards.map((card, index) => {
              const Icon = LucideIcons[card.icon_name] || LucideIcons.HelpCircle;
              return (
                <motion.div
                  key={card.id || index}
                  whileHover={{ y: -8 }}
                  transition={{ duration: 0.3 }}
                  className={`bg-gradient-to-br ${card.gradient_from} rounded-2xl p-6 text-center text-white shadow-lg`}
                >
                  <div className="flex justify-center mb-4"><Icon className="w-8 h-8 text-white" /></div>
                  <h3 className="text-lg font-semibold mb-1">{card.title}</h3>
                  <p className="text-3xl font-bold">{card.description}</p>
                </motion.div>
              )
            })
          ) : (
            <div className="col-span-4 text-center text-gray-500">No summary cards available.</div>
          )
        )}
      </section>

      {/* ===== Table Section ===== */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="overflow-x-auto shadow-2xl rounded-2xl border border-gray-100">
          <table className="w-full border-collapse text-center text-sm md:text-base">
            <thead className="bg-[#2d347c] text-white">
              <tr>
                <th className="p-4">Category</th>
                <th className="p-4">Closed (Units)</th>
                <th className="p-4">Closed (%)</th>
                <th className="p-4">Live (Units)</th>
                <th className="p-4">Live (%)</th>
                <th className="p-4">Risk Pool (Units)</th>
                <th className="p-4">Risk Pool (%)</th>
              </tr>
            </thead>
            <tbody>
              {tableData.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-slate-500">No data available</td></tr>
              ) : (
                tableData.map((row, index) => (
                  <motion.tr
                    key={row.id || index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className={`${index === tableData.length - 1
                      ? "bg-blue-50 font-semibold text-[#2d347c]"
                      : "bg-white"
                      } hover:bg-blue-100 transition-colors`}
                  >
                    <td className="p-4 border-t">{row.category}</td>
                    <td className="p-4 border-t">{row.closed_units}</td>
                    <td className="p-4 border-t">{row.closed_percent}</td>
                    <td className="p-4 border-t">{row.live_units}</td>
                    <td className="p-4 border-t">{row.live_percent}</td>
                    <td className="p-4 border-t">{row.risk_pool_units}</td>
                    <td className="p-4 border-t">{row.risk_pool_percent}</td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default RiskClassification;
